import type { ReactNode } from "react";

import { CopyUserMessageButton } from "@product/components/app/runs/copy-user-message-button";
import {
  ProvenanceAvatarStack,
  type ProvenanceActor,
} from "@product/components/app/runs/provenance-avatar-stack";
import { RunSourcesUsedView } from "@product/components/app/runs/run-sources-used-view";
import type { PluginIconPlugin } from "@product/components/content/plugin-icon";
import type { RunSourceUsage } from "@product/lib/run-source-usage";
import {
  AssistantMessageColumn,
  RunProvenanceRow,
  TranscriptTurnView,
  UserMessageTurn,
} from "@product/components/app/runs/transcript-turn-view";

import { USER } from "./fixture";
import { RUN_PLUGINS, RUN_SOURCES } from "./fixture-run";
import { ShotMarkdown } from "./markdown";
import type { ShotStory } from "./story";
import { OSTREY_TURN_TIMES, type ShotTurnTimes } from "./workspace";

/** The user's side of a turn: the question in the chat bubble. */
export function ShotUserTurn({
  question,
  user = USER,
  times = OSTREY_TURN_TIMES,
}: {
  question: string;
  user?: ProvenanceActor;
  times?: ShotTurnTimes;
}) {
  return (
    <UserMessageTurn
      dateTime={new Date(times.askedAt).toISOString()}
      dateTitle={times.dateTitle}
      timeLabel={times.timeLabel}
      provenance={
        <ProvenanceAvatarStack actor={user} interfaceType="web" action="started" />
      }
      actions={<CopyUserMessageButton text={question} />}
    >
      <ShotMarkdown className="max-w-none">{question}</ShotMarkdown>
    </UserMessageTurn>
  );
}

/**
 * Beneath a finished answer: the Twin retrieved it, with these sources. An
 * answer that has just completed in view keeps the Twin mark working for
 * ten seconds, as the transcript does (`workingSeconds`).
 */
export function ShotProvenance({
  workingSeconds,
  sources = RUN_SOURCES,
  plugins = RUN_PLUGINS,
  user = USER,
}: {
  workingSeconds?: number;
  sources?: RunSourceUsage[];
  plugins?: PluginIconPlugin[];
  user?: ProvenanceActor;
} = {}) {
  return (
    <RunProvenanceRow>
      <ProvenanceAvatarStack
        actor={user}
        interfaceType="web"
        usedTwin
        workingSeconds={workingSeconds}
        action="retrieved"
      />
      <RunSourcesUsedView
        sources={sources}
        plugins={plugins}
        className="max-w-[800px]"
      />
    </RunProvenanceRow>
  );
}

/**
 * One finished turn of the session, as the transcript renders it: the
 * question, the answer and its provenance. `answer` defaults to the
 * rendered Markdown; interactive shots pass their own answer node.
 */
export function ShotTurn({
  question,
  answerMarkdown,
  answer,
  provenance = true,
  sources,
  plugins,
  user,
  times,
}: {
  question: string;
  answerMarkdown?: string;
  answer?: ReactNode;
  provenance?: boolean;
  /** The run's sources, when the turn is not the kettle comparison. */
  sources?: RunSourceUsage[];
  plugins?: PluginIconPlugin[];
  /** Who asked, and when; the kettle question's by default. */
  user?: ProvenanceActor;
  times?: ShotTurnTimes;
}) {
  return (
    <TranscriptTurnView user={<ShotUserTurn question={question} user={user} times={times} />}>
      <AssistantMessageColumn>
        {answer ?? <ShotMarkdown>{answerMarkdown ?? ""}</ShotMarkdown>}
        {provenance ? <ShotProvenance sources={sources} plugins={plugins} user={user} /> : null}
      </AssistantMessageColumn>
    </TranscriptTurnView>
  );
}

/** A story's finished turn: its question, its answer and, when the run's
 *  use of them is known, the sources beneath it. */
export function ShotStoryTurn({ story, answer }: { story: ShotStory; answer?: ReactNode }) {
  return (
    <ShotTurn
      question={story.question}
      answerMarkdown={story.answerMarkdown}
      answer={answer}
      user={story.workspace.user}
      times={story.times}
      provenance={story.provenance !== null}
      sources={story.provenance?.sources}
      plugins={story.provenance?.plugins}
    />
  );
}
